import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    useClientSubscriptionCreate,
} from '../../processes/client_subscription_create';

export default function ClientRegistrationDialog({dialog, companies, activeClientSubscriptions, onSuccess}) {
    const {isSuccess: ok, mutate, isError, error, isPending, reset} = useClientSubscriptionCreate();
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState('');
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [formError, setFormError] = useState('');
    const [open, setOpen] = useState(false);

    useEffect(() => {
        reset();
        resetForm();
    }, [reset]);

    useEffect(() => {
        if (ok) {
            dialog.current?.close();
            onSuccess?.();
            resetForm();
            setTimeout(() => reset(), 100);
        }
    }, [ok, dialog, onSuccess, reset]);

    const resetForm = useCallback(() => {
        setSearch('');
        setSelected('');
        setStart('');
        setEnd('');
        setFormError('');
        setOpen(false);
    }, []);

    const submit = useCallback(() => {
        if (!selected || !start || !end) return setFormError('All fields are required');
        if (new Date(start) > new Date(end)) return setFormError('Start date cannot be later than end date');
        if (activeClientSubscriptions.has(selected)) return setFormError('Client has active subscription. Please deactivate first before creating a new one.');

        setFormError('');
        mutate({
            method: 'POST',
            body: JSON.stringify({clientId: selected, startDate: start, endDate: end}),
            headers: {'Content-Type': 'application/json'},
        });
    }, [selected, start, end, mutate, activeClientSubscriptions]);

    const handleDialogClose = useCallback(() => {
        resetForm();
        reset();
    }, [resetForm, reset]);

    const filteredCompanies = useMemo(() => {
        if (!search || !companies.length) return companies;
        return companies.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
    }, [companies, search]);

    const selectedClientHasActiveSubscription = selected && activeClientSubscriptions.has(selected);

    return (
        <dialog ref={dialog} className="tw-modal tw-modal-middle" onClose={handleDialogClose}>
            <div className="tw-modal-box tw-w-11/12 tw-max-w-2xl">
                <div className="tw-bg-primary tw-text-primary-content tw-p-4 tw-rounded-t-lg tw--m-6 tw-mb-6">
                    <h3 className="tw-font-bold tw-text-lg">Client Registration</h3>
                </div>

                {(formError || isError) && (
                    <div className="tw-alert tw-alert-error tw-mb-4">
                        <span>{formError || error?.message || 'An error occurred'}</span></div>
                )}

                {selectedClientHasActiveSubscription && (
                    <div className="tw-alert tw-alert-warning tw-mb-4">
                        <span>⚠️ This client has an active subscription</span></div>
                )}

                <div className="tw-space-y-4">
                    <div className="tw-form-control tw-relative">
                        <label className="tw-label"><span
                            className="tw-label-text">Client ({companies.length} available)</span></label>
                        <input
                            value={search}
                            placeholder="Type to search..."
                            className="tw-input tw-input-bordered tw-w-full"
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setSelected('');
                                setOpen(true);
                            }}
                            onFocus={() => setOpen(true)}
                            onBlur={() => setTimeout(() => setOpen(false), 200)}
                        />
                        {open && search && filteredCompanies.length > 0 ? (
                            <div
                                className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-bg-base-100 tw-border tw-rounded-lg tw-shadow-lg tw-max-h-48 tw-overflow-y-auto tw-z-50 tw-mt-1">
                                {filteredCompanies.map((c) => (
                                    <div
                                        key={c.id}
                                        className={`tw-px-4 tw-py-3 hover:tw-bg-base-200 tw-cursor-pointer tw-flex tw-items-center tw-justify-between ${activeClientSubscriptions.has(c.id) ? 'tw-bg-yellow-50' : ''}`}
                                        onMouseDown={(e) => e.preventDefault()}
                                        onClick={() => {
                                            setSelected(c.id);
                                            setSearch(c.name);
                                            setOpen(false);
                                        }}
                                    >
                                        <span>(...{c.id.slice(-5)}) {c.name}</span>
                                        {activeClientSubscriptions.has(c.id) && (
                                            <span className="tw-badge tw-badge-warning tw-badge-sm">Active</span>)}
                                    </div>
                                ))}
                            </div>
                        ) : open && search ? (
                            <div
                                className="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-bg-base-100 tw-border tw-rounded-lg tw-shadow-lg tw-p-4 tw-z-50 tw-mt-1">
                                <span className="tw-text-gray-500">No companies found</span>
                            </div>
                        ) : null}
                    </div>

                    <div className="tw-form-control">
                        <label className="tw-label"><span className="tw-label-text">Start Date</span></label>
                        <input type="date" className="tw-input tw-input-bordered tw-w-full" value={start}
                               onChange={(e) => setStart(e.target.value)}/>
                    </div>

                    <div className="tw-form-control">
                        <label className="tw-label"><span className="tw-label-text">End Date</span></label>
                        <input type="date" className="tw-input tw-input-bordered tw-w-full" value={end}
                               onChange={(e) => setEnd(e.target.value)} min={start}/>
                    </div>

                    <div className="tw-flex tw-space-x-3">
                        <button className="tw-btn tw-btn-outline tw-flex-1"
                                onClick={() => dialog.current?.close()}>Cancel
                        </button>
                        <button className="tw-btn tw-btn-primary tw-flex-1"
                                disabled={!selected || !start || !end || isPending || selectedClientHasActiveSubscription}
                                onClick={submit}>
                            {isPending ? (<><span
                                className="tw-loading tw-loading-spinner tw-loading-sm"/>Creating...</>) : 'Submit'}
                        </button>
                    </div>
                </div>
            </div>
            <div className="tw-modal-backdrop" onClick={(e) => {
                e.preventDefault();
                dialog.current?.close();
            }}/>
        </dialog>
    );
}
